"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.applyTransform = applyTransform;
exports.approximatelyEquals = approximatelyEquals;
exports.boundingExtent = boundingExtent;
exports.buffer = buffer;
exports.clone = clone;
exports.closestSquaredDistanceXY = closestSquaredDistanceXY;
exports.containsCoordinate = containsCoordinate;
exports.containsExtent = containsExtent;
exports.containsXY = containsXY;
exports.coordinateRelationship = coordinateRelationship;
exports.createEmpty = createEmpty;
exports.createOrUpdate = createOrUpdate;
exports.createOrUpdateEmpty = createOrUpdateEmpty;
exports.createOrUpdateFromCoordinate = createOrUpdateFromCoordinate;
exports.createOrUpdateFromCoordinates = createOrUpdateFromCoordinates;
exports.createOrUpdateFromFlatCoordinates = createOrUpdateFromFlatCoordinates;
exports.createOrUpdateFromRings = createOrUpdateFromRings;
exports.equals = equals;
exports.extend = extend;
exports.extendCoordinate = extendCoordinate;
exports.extendCoordinates = extendCoordinates;
exports.extendFlatCoordinates = extendFlatCoordinates;
exports.extendRings = extendRings;
exports.extendXY = extendXY;
exports.forEachCorner = forEachCorner;
exports.getArea = getArea;
exports.getBottomLeft = getBottomLeft;
exports.getBottomRight = getBottomRight;
exports.getCenter = getCenter;
exports.getCorner = getCorner;
exports.getEnlargedArea = getEnlargedArea;
exports.getForViewAndSize = getForViewAndSize;
exports.getHeight = getHeight;
exports.getIntersection = getIntersection;
exports.getIntersectionArea = getIntersectionArea;
exports.getMargin = getMargin;
exports.getSize = getSize;
exports.getTopLeft = getTopLeft;
exports.getTopRight = getTopRight;
exports.getWidth = getWidth;
exports.intersects = intersects;
exports.intersectsSegment = intersectsSegment;
exports.isEmpty = isEmpty;
exports.returnOrUpdate = returnOrUpdate;
exports.scaleFromCenter = scaleFromCenter;
exports.wrapX = wrapX;

var _Corner = _interopRequireDefault(require("./extent/Corner.js"));

var _Relationship = _interopRequireDefault(require("./extent/Relationship.js"));

var _asserts = require("./asserts.js");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }

/**
 * @module ol/extent
 */

/**
 * An array of numbers representing an extent: `[minx, miny, maxx, maxy]`.
 * @typedef {Array<number>} Extent
 * @api
 */

/**
 * Build an extent that includes all given coordinates.
 *
 * @param {Array<import("./coordinate.js").Coordinate>} coordinates Coordinates.
 * @return {Extent} Bounding extent.
 * @api
 */
function boundingExtent(coordinates) {
  var extent = createEmpty();

  for (var i = 0, ii = coordinates.length; i < ii; ++i) {
    extendCoordinate(extent, coordinates[i]);
  }

  return extent;
}
/**
 * @param {Array<number>} xs Xs.
 * @param {Array<number>} ys Ys.
 * @param {Extent} [opt_extent] Destination extent.
 * @private
 * @return {Extent} Extent.
 */


function _boundingExtentXYs(xs, ys, opt_extent) {
  var minX = Math.min.apply(null, xs);
  var minY = Math.min.apply(null, ys);
  var maxX = Math.max.apply(null, xs);
  var maxY = Math.max.apply(null, ys);
  return createOrUpdate(minX, minY, maxX, maxY, opt_extent);
}
/**
 * Return extent increased by the provided value.
 * @param {Extent} extent Extent.
 * @param {number} value The amount by which the extent should be buffered.
 * @param {Extent} [opt_extent] Extent.
 * @return {Extent} Extent.
 * @api
 */


function buffer(extent, value, opt_extent) {
  if (opt_extent) {
    opt_extent[0] = extent[0] - value;
    opt_extent[1] = extent[1] - value;
    opt_extent[2] = extent[2] + value;
    opt_extent[3] = extent[3] + value;
    return opt_extent;
  } else {
    return [extent[0] - value, extent[1] - value, extent[2] + value, extent[3] + value];
  }
}
/**
 * Creates a clone of an extent.
 *
 * @param {Extent} extent Extent to clone.
 * @param {Extent} [opt_extent] Extent.
 * @return {Extent} The clone.
 */


function clone(extent, opt_extent) {
  if (opt_extent) {
    opt_extent[0] = extent[0];
    opt_extent[1] = extent[1];
    opt_extent[2] = extent[2];
    opt_extent[3] = extent[3];
    return opt_extent;
  } else {
    return extent.slice();
  }
}
/**
 * @param {Extent} extent Extent.
 * @param {number} x X.
 * @param {number} y Y.
 * @return {number} Closest squared distance.
 */


function closestSquaredDistanceXY(extent, x, y) {
  var dx, dy;

  if (x < extent[0]) {
    dx = extent[0] - x;
  } else if (extent[2] < x) {
    dx = x - extent[2];
  } else {
    dx = 0;
  }

  if (y < extent[1]) {
    dy = extent[1] - y;
  } else if (extent[3] < y) {
    dy = y - extent[3];
  } else {
    dy = 0;
  }

  return dx * dx + dy * dy;
}
/**
 * Check if the passed coordinate is contained or on the edge of the extent.
 *
 * @param {Extent} extent Extent.
 * @param {import("./coordinate.js").Coordinate} coordinate Coordinate.
 * @return {boolean} The coordinate is contained in the extent.
 * @api
 */


function containsCoordinate(extent, coordinate) {
  return containsXY(extent, coordinate[0], coordinate[1]);
}
/**
 * Check if one extent contains another.
 *
 * An extent is deemed contained if it lies completely within the other extent,
 * including if they share one or more edges.
 *
 * @param {Extent} extent1 Extent 1.
 * @param {Extent} extent2 Extent 2.
 * @return {boolean} The second extent is contained by or on the edge of the
 *     first.
 * @api
 */


function containsExtent(extent1, extent2) {
  return extent1[0] <= extent2[0] && extent2[2] <= extent1[2] && extent1[1] <= extent2[1] && extent2[3] <= extent1[3];
}
/**
 * Check if the passed coordinate is contained or on the edge of the extent.
 *
 * @param {Extent} extent Extent.
 * @param {number} x X coordinate.
 * @param {number} y Y coordinate.
 * @return {boolean} The x, y values are contained in the extent.
 * @api
 */


function containsXY(extent, x, y) {
  return extent[0] <= x && x <= extent[2] && extent[1] <= y && y <= extent[3];
}
/**
 * Get the relationship between a coordinate and extent.
 * @param {Extent} extent The extent.
 * @param {import("./coordinate.js").Coordinate} coordinate The coordinate.
 * @return {import("./extent/Relationship.js").default} The relationship (bitwise compare with
 *     import("./extent/Relationship.js").Relationship).
 */


function coordinateRelationship(extent, coordinate) {
  var minX = extent[0];
  var minY = extent[1];
  var maxX = extent[2];
  var maxY = extent[3];
  var x = coordinate[0];
  var y = coordinate[1];
  var relationship = _Relationship["default"].UNKNOWN;

  if (x < minX) {
    relationship = relationship | _Relationship["default"].LEFT;
  } else if (x > maxX) {
    relationship = relationship | _Relationship["default"].RIGHT;
  }

  if (y < minY) {
    relationship = relationship | _Relationship["default"].BELOW;
  } else if (y > maxY) {
    relationship = relationship | _Relationship["default"].ABOVE;
  }

  if (relationship === _Relationship["default"].UNKNOWN) {
    relationship = _Relationship["default"].INTERSECTING;
  }

  return relationship;
}
/**
 * Create an empty extent.
 * @return {Extent} Empty extent.
 * @api
 */


function createEmpty() {
  return [Infinity, Infinity, -Infinity, -Infinity];
}
/**
 * Create a new extent or update the provided extent.
 * @param {number} minX Minimum X.
 * @param {number} minY Minimum Y.
 * @param {number} maxX Maximum X.
 * @param {number} maxY Maximum Y.
 * @param {Extent} [opt_extent] Destination extent.
 * @return {Extent} Extent.
 */


function createOrUpdate(minX, minY, maxX, maxY, opt_extent) {
  if (opt_extent) {
    opt_extent[0] = minX;
    opt_extent[1] = minY;
    opt_extent[2] = maxX;
    opt_extent[3] = maxY;
    return opt_extent;
  } else {
    return [minX, minY, maxX, maxY];
  }
}
/**
 * Create a new empty extent or make the provided one empty.
 * @param {Extent} [opt_extent] Extent.
 * @return {Extent} Extent.
 */


function createOrUpdateEmpty(opt_extent) {
  return createOrUpdate(Infinity, Infinity, -Infinity, -Infinity, opt_extent);
}
/**
 * @param {import("./coordinate.js").Coordinate} coordinate Coordinate.
 * @param {Extent} [opt_extent] Extent.
 * @return {Extent} Extent.
 */


function createOrUpdateFromCoordinate(coordinate, opt_extent) {
  var x = coordinate[0];
  var y = coordinate[1];
  return createOrUpdate(x, y, x, y, opt_extent);
}
/**
 * @param {Array<import("./coordinate.js").Coordinate>} coordinates Coordinates.
 * @param {Extent} [opt_extent] Extent.
 * @return {Extent} Extent.
 */


function createOrUpdateFromCoordinates(coordinates, opt_extent) {
  var extent = createOrUpdateEmpty(opt_extent);
  return extendCoordinates(extent, coordinates);
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @param {Extent} [opt_extent] Extent.
 * @return {Extent} Extent.
 */


function createOrUpdateFromFlatCoordinates(flatCoordinates, offset, end, stride, opt_extent) {
  var extent = createOrUpdateEmpty(opt_extent);
  return extendFlatCoordinates(extent, flatCoordinates, offset, end, stride);
}
/**
 * @param {Array<Array<import("./coordinate.js").Coordinate>>} rings Rings.
 * @param {Extent} [opt_extent] Extent.
 * @return {Extent} Extent.
 */


function createOrUpdateFromRings(rings, opt_extent) {
  var extent = createOrUpdateEmpty(opt_extent);
  return extendRings(extent, rings);
}
/**
 * Determine if two extents are equivalent.
 * @param {Extent} extent1 Extent 1.
 * @param {Extent} extent2 Extent 2.
 * @return {boolean} The two extents are equivalent.
 * @api
 */


function equals(extent1, extent2) {
  return extent1[0] == extent2[0] && extent1[2] == extent2[2] && extent1[1] == extent2[1] && extent1[3] == extent2[3];
}
/**
 * Determine if two extents are approximately equivalent.
 * @param {Extent} extent1 Extent 1.
 * @param {Extent} extent2 Extent 2.
 * @param {number} tolerance Tolerance in extent coordinate units.
 * @return {boolean} The two extents differ by less than the tolerance.
 */


function approximatelyEquals(extent1, extent2, tolerance) {
  return Math.abs(extent1[0] - extent2[0]) < tolerance && Math.abs(extent1[2] - extent2[2]) < tolerance && Math.abs(extent1[1] - extent2[1]) < tolerance && Math.abs(extent1[3] - extent2[3]) < tolerance;
}
/**
 * Modify an extent to include another extent.
 * @param {Extent} extent1 The extent to be modified.
 * @param {Extent} extent2 The extent that will be included in the first.
 * @return {Extent} A reference to the first (extended) extent.
 * @api
 */


function extend(extent1, extent2) {
  if (extent2[0] < extent1[0]) {
    extent1[0] = extent2[0];
  }

  if (extent2[2] > extent1[2]) {
    extent1[2] = extent2[2];
  }

  if (extent2[1] < extent1[1]) {
    extent1[1] = extent2[1];
  }

  if (extent2[3] > extent1[3]) {
    extent1[3] = extent2[3];
  }

  return extent1;
}
/**
 * @param {Extent} extent Extent.
 * @param {import("./coordinate.js").Coordinate} coordinate Coordinate.
 */


function extendCoordinate(extent, coordinate) {
  if (coordinate[0] < extent[0]) {
    extent[0] = coordinate[0];
  }

  if (coordinate[0] > extent[2]) {
    extent[2] = coordinate[0];
  }

  if (coordinate[1] < extent[1]) {
    extent[1] = coordinate[1];
  }

  if (coordinate[1] > extent[3]) {
    extent[3] = coordinate[1];
  }
}
/**
 * @param {Extent} extent Extent.
 * @param {Array<import("./coordinate.js").Coordinate>} coordinates Coordinates.
 * @return {Extent} Extent.
 */


function extendCoordinates(extent, coordinates) {
  for (var i = 0, ii = coordinates.length; i < ii; ++i) {
    extendCoordinate(extent, coordinates[i]);
  }

  return extent;
}
/**
 * @param {Extent} extent Extent.
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @return {Extent} Extent.
 */


function extendFlatCoordinates(extent, flatCoordinates, offset, end, stride) {
  for (; offset < end; offset += stride) {
    extendXY(extent, flatCoordinates[offset], flatCoordinates[offset + 1]);
  }

  return extent;
}
/**
 * @param {Extent} extent Extent.
 * @param {Array<Array<import("./coordinate.js").Coordinate>>} rings Rings.
 * @return {Extent} Extent.
 */


function extendRings(extent, rings) {
  for (var i = 0, ii = rings.length; i < ii; ++i) {
    extendCoordinates(extent, rings[i]);
  }

  return extent;
}
/**
 * @param {Extent} extent Extent.
 * @param {number} x X.
 * @param {number} y Y.
 */


function extendXY(extent, x, y) {
  extent[0] = Math.min(extent[0], x);
  extent[1] = Math.min(extent[1], y);
  extent[2] = Math.max(extent[2], x);
  extent[3] = Math.max(extent[3], y);
}
/**
 * This function calls `callback` for each corner of the extent. If the
 * callback returns a truthy value the function returns that value
 * immediately. Otherwise the function returns `false`.
 * @param {Extent} extent Extent.
 * @param {function(import("./coordinate.js").Coordinate): S} callback Callback.
 * @return {S|boolean} Value.
 * @template S
 */


function forEachCorner(extent, callback) {
  var val;
  val = callback(getBottomLeft(extent));

  if (val) {
    return val;
  }

  val = callback(getBottomRight(extent));

  if (val) {
    return val;
  }

  val = callback(getTopRight(extent));

  if (val) {
    return val;
  }

  val = callback(getTopLeft(extent));

  if (val) {
    return val;
  }

  return false;
}
/**
 * Get the size of an extent.
 * @param {Extent} extent Extent.
 * @return {number} Area.
 * @api
 */


function getArea(extent) {
  var area = 0;

  if (!isEmpty(extent)) {
    area = getWidth(extent) * getHeight(extent);
  }

  return area;
}
/**
 * Get the bottom left coordinate of an extent.
 * @param {Extent} extent Extent.
 * @return {import("./coordinate.js").Coordinate} Bottom left coordinate.
 * @api
 */


function getBottomLeft(extent) {
  return [extent[0], extent[1]];
}
/**
 * Get the bottom right coordinate of an extent.
 * @param {Extent} extent Extent.
 * @return {import("./coordinate.js").Coordinate} Bottom right coordinate.
 * @api
 */


function getBottomRight(extent) {
  return [extent[2], extent[1]];
}
/**
 * Get the center coordinate of an extent.
 * @param {Extent} extent Extent.
 * @return {import("./coordinate.js").Coordinate} Center.
 * @api
 */


function getCenter(extent) {
  return [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
}
/**
 * Get a corner coordinate of an extent.
 * @param {Extent} extent Extent.
 * @param {import("./extent/Corner.js").default} corner Corner.
 * @return {import("./coordinate.js").Coordinate} Corner coordinate.
 */


function getCorner(extent, corner) {
  var coordinate;

  if (corner === _Corner["default"].BOTTOM_LEFT) {
    coordinate = getBottomLeft(extent);
  } else if (corner === _Corner["default"].BOTTOM_RIGHT) {
    coordinate = getBottomRight(extent);
  } else if (corner === _Corner["default"].TOP_LEFT) {
    coordinate = getTopLeft(extent);
  } else if (corner === _Corner["default"].TOP_RIGHT) {
    coordinate = getTopRight(extent);
  } else {
    (0, _asserts.assert)(false, 13); // Invalid corner
  }

  return coordinate;
}
/**
 * @param {Extent} extent1 Extent 1.
 * @param {Extent} extent2 Extent 2.
 * @return {number} Enlarged area.
 */


function getEnlargedArea(extent1, extent2) {
  var minX = Math.min(extent1[0], extent2[0]);
  var minY = Math.min(extent1[1], extent2[1]);
  var maxX = Math.max(extent1[2], extent2[2]);
  var maxY = Math.max(extent1[3], extent2[3]);
  return (maxX - minX) * (maxY - minY);
}
/**
 * @param {import("./coordinate.js").Coordinate} center Center.
 * @param {number} resolution Resolution.
 * @param {number} rotation Rotation.
 * @param {import("./size.js").Size} size Size.
 * @param {Extent} [opt_extent] Destination extent.
 * @return {Extent} Extent.
 */


function getForViewAndSize(center, resolution, rotation, size, opt_extent) {
  var dx = resolution * size[0] / 2;
  var dy = resolution * size[1] / 2;
  var cosRotation = Math.cos(rotation);
  var sinRotation = Math.sin(rotation);
  var xCos = dx * cosRotation;
  var xSin = dx * sinRotation;
  var yCos = dy * cosRotation;
  var ySin = dy * sinRotation;
  var x = center[0];
  var y = center[1];
  var x0 = x - xCos + ySin;
  var x1 = x - xCos - ySin;
  var x2 = x + xCos - ySin;
  var x3 = x + xCos + ySin;
  var y0 = y - xSin - yCos;
  var y1 = y - xSin + yCos;
  var y2 = y + xSin + yCos;
  var y3 = y + xSin - yCos;
  return createOrUpdate(Math.min(x0, x1, x2, x3), Math.min(y0, y1, y2, y3), Math.max(x0, x1, x2, x3), Math.max(y0, y1, y2, y3), opt_extent);
}
/**
 * Get the height of an extent.
 * @param {Extent} extent Extent.
 * @return {number} Height.
 * @api
 */


function getHeight(extent) {
  return extent[3] - extent[1];
}
/**
 * @param {Extent} extent1 Extent 1.
 * @param {Extent} extent2 Extent 2.
 * @return {number} Intersection area.
 */


function getIntersectionArea(extent1, extent2) {
  var intersection = getIntersection(extent1, extent2);
  return getArea(intersection);
}
/**
 * Get the intersection of two extents.
 * @param {Extent} extent1 Extent 1.
 * @param {Extent} extent2 Extent 2.
 * @param {Extent} [opt_extent] Optional extent to populate with intersection.
 * @return {Extent} Intersecting extent.
 * @api
 */


function getIntersection(extent1, extent2, opt_extent) {
  var intersection = opt_extent ? opt_extent : createEmpty();

  if (intersects(extent1, extent2)) {
    if (extent1[0] > extent2[0]) {
      intersection[0] = extent1[0];
    } else {
      intersection[0] = extent2[0];
    }

    if (extent1[1] > extent2[1]) {
      intersection[1] = extent1[1];
    } else {
      intersection[1] = extent2[1];
    }

    if (extent1[2] < extent2[2]) {
      intersection[2] = extent1[2];
    } else {
      intersection[2] = extent2[2];
    }

    if (extent1[3] < extent2[3]) {
      intersection[3] = extent1[3];
    } else {
      intersection[3] = extent2[3];
    }
  } else {
    createOrUpdateEmpty(intersection);
  }

  return intersection;
}
/**
 * @param {Extent} extent Extent.
 * @return {number} Margin.
 */


function getMargin(extent) {
  return getWidth(extent) + getHeight(extent);
}
/**
 * Get the size (width, height) of an extent.
 * @param {Extent} extent The extent.
 * @return {import("./size.js").Size} The extent size.
 * @api
 */


function getSize(extent) {
  return [extent[2] - extent[0], extent[3] - extent[1]];
}
/**
 * Get the top left coordinate of an extent.
 * @param {Extent} extent Extent.
 * @return {import("./coordinate.js").Coordinate} Top left coordinate.
 * @api
 */


function getTopLeft(extent) {
  return [extent[0], extent[3]];
}
/**
 * Get the top right coordinate of an extent.
 * @param {Extent} extent Extent.
 * @return {import("./coordinate.js").Coordinate} Top right coordinate.
 * @api
 */


function getTopRight(extent) {
  return [extent[2], extent[3]];
}
/**
 * Get the width of an extent.
 * @param {Extent} extent Extent.
 * @return {number} Width.
 * @api
 */


function getWidth(extent) {
  return extent[2] - extent[0];
}
/**
 * Determine if one extent intersects another.
 * @param {Extent} extent1 Extent 1.
 * @param {Extent} extent2 Extent.
 * @return {boolean} The two extents intersect.
 * @api
 */


function intersects(extent1, extent2) {
  return extent1[0] <= extent2[2] && extent1[2] >= extent2[0] && extent1[1] <= extent2[3] && extent1[3] >= extent2[1];
}
/**
 * Determine if an extent is empty.
 * @param {Extent} extent Extent.
 * @return {boolean} Is empty.
 * @api
 */


function isEmpty(extent) {
  return extent[2] < extent[0] || extent[3] < extent[1];
}
/**
 * @param {Extent} extent Extent.
 * @param {Extent} [opt_extent] Extent.
 * @return {Extent} Extent.
 */


function returnOrUpdate(extent, opt_extent) {
  if (opt_extent) {
    opt_extent[0] = extent[0];
    opt_extent[1] = extent[1];
    opt_extent[2] = extent[2];
    opt_extent[3] = extent[3];
    return opt_extent;
  } else {
    return extent;
  }
}
/**
 * @param {Extent} extent Extent.
 * @param {number} value Value.
 */


function scaleFromCenter(extent, value) {
  var deltaX = (extent[2] - extent[0]) / 2 * (value - 1);
  var deltaY = (extent[3] - extent[1]) / 2 * (value - 1);
  extent[0] -= deltaX;
  extent[2] += deltaX;
  extent[1] -= deltaY;
  extent[3] += deltaY;
}
/**
 * Determine if the segment between two coordinates intersects (crosses,
 * touches, or is contained by) the provided extent.
 * @param {Extent} extent The extent.
 * @param {import("./coordinate.js").Coordinate} start Segment start coordinate.
 * @param {import("./coordinate.js").Coordinate} end Segment end coordinate.
 * @return {boolean} The segment intersects the extent.
 */


function intersectsSegment(extent, start, end) {
  var intersects = false;
  var startRel = coordinateRelationship(extent, start);
  var endRel = coordinateRelationship(extent, end);

  if (startRel === _Relationship["default"].INTERSECTING || endRel === _Relationship["default"].INTERSECTING) {
    intersects = true;
  } else {
    var minX = extent[0];
    var minY = extent[1];
    var maxX = extent[2];
    var maxY = extent[3];
    var startX = start[0];
    var startY = start[1];
    var endX = end[0];
    var endY = end[1];
    var slope = (endY - startY) / (endX - startX);
    var x = void 0,
        y = void 0;

    if (!!(endRel & _Relationship["default"].ABOVE) && !(startRel & _Relationship["default"].ABOVE)) {
      // potentially intersects top
      x = endX - (endY - maxY) / slope;
      intersects = x >= minX && x <= maxX;
    }

    if (!intersects && !!(endRel & _Relationship["default"].RIGHT) && !(startRel & _Relationship["default"].RIGHT)) {
      // potentially intersects right
      y = endY - (endX - maxX) * slope;
      intersects = y >= minY && y <= maxY;
    }

    if (!intersects && !!(endRel & _Relationship["default"].BELOW) && !(startRel & _Relationship["default"].BELOW)) {
      // potentially intersects bottom
      x = endX - (endY - minY) / slope;
      intersects = x >= minX && x <= maxX;
    }

    if (!intersects && !!(endRel & _Relationship["default"].LEFT) && !(startRel & _Relationship["default"].LEFT)) {
      // potentially intersects left
      y = endY - (endX - minX) * slope;
      intersects = y >= minY && y <= maxY;
    }
  }

  return intersects;
}
/**
 * Apply a transform function to the extent.
 * @param {Extent} extent Extent.
 * @param {import("./proj.js").TransformFunction} transformFn Transform function.
 * Called with `[minX, minY, maxX, maxY]` extent coordinates.
 * @param {Extent} [opt_extent] Destination extent.
 * @param {number} [opt_stops] Number of stops per side used for the transform.
 * By default only the corners are used.
 * @return {Extent} Extent.
 * @api
 */


function applyTransform(extent, transformFn, opt_extent, opt_stops) {
  var coordinates = [];

  if (opt_stops > 1) {
    var width = extent[2] - extent[0];
    var height = extent[3] - extent[1];

    for (var i = 0; i < opt_stops; ++i) {
      coordinates.push(extent[0] + width * i / opt_stops, extent[1], extent[2], extent[1] + height * i / opt_stops, extent[2] - width * i / opt_stops, extent[3], extent[0], extent[3] - height * i / opt_stops);
    }
  } else {
    coordinates = [extent[0], extent[1], extent[2], extent[1], extent[2], extent[3], extent[0], extent[3]];
  }

  transformFn(coordinates, coordinates, 2);
  var xs = [];
  var ys = [];

  for (var i = 0, l = coordinates.length; i < l; i += 2) {
    xs.push(coordinates[i]);
    ys.push(coordinates[i + 1]);
  }

  return _boundingExtentXYs(xs, ys, opt_extent);
}
/**
 * Modifies the provided extent in-place to be within the real world
 * extent.
 *
 * @param {Extent} extent Extent.
 * @param {import("./proj/Projection.js").default} projection Projection
 * @return {Extent} The extent within the real world extent.
 */


function wrapX(extent, projection) {
  var projectionExtent = projection.getExtent();
  var center = getCenter(extent);

  if (projection.canWrapX() && (center[0] < projectionExtent[0] || center[0] >= projectionExtent[2])) {
    var worldWidth = getWidth(projectionExtent);
    var worldsAway = Math.floor((center[0] - projectionExtent[0]) / worldWidth);
    var offset = worldsAway * worldWidth;
    extent[0] -= offset;
    extent[2] -= offset;
  }

  return extent;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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